import { useState, useEffect } from "react";
import { api, errMsg } from "../../lib/api";
import { Input } from "../../components/ui/input";
import { Button } from "../../components/ui/button";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "../../components/ui/select";
import { toast } from "sonner";
import { ExternalLink } from "lucide-react";

function Row({ r, onSaved }) {
  const [slot, setSlot] = useState("judge1");
  const [s, setS] = useState({ teamwork: "", creativity: "", teaches: "" });
  const [url, setUrl] = useState(r.video_url || "");

  const saveUrl = async () => {
    try {
      await api.put(`/admin/reels/${r.property_id}/url`, { video_url: url });
      toast.success("Reel URL saved");
      onSaved();
    } catch (err) { toast.error(errMsg(err)); }
  };

  const saveScore = async () => {
    try {
      await api.post(`/admin/reels/${r.property_id}/score`, {
        judge_slot: slot, teamwork: parseFloat(s.teamwork), creativity: parseFloat(s.creativity), teaches: parseFloat(s.teaches), video_url: url || null,
      });
      toast.success(`Saved ${slot} score for ${r.property_name}`);
      onSaved();
    } catch (err) { toast.error(errMsg(err)); }
  };

  return (
    <div className="border border-slate-200 rounded-md p-3" data-testid={`admin-reel-row-${r.property_id}`}>
      <div className="flex flex-wrap items-center gap-3">
        <div className="min-w-48 flex-1">
          <div className="font-medium text-sm text-[#1a1f36]">{r.property_name}</div>
          <div className="text-xs text-slate-500">{r.division} · {r.tier} · {Object.keys(r.scores).length}/3 judges · avg {r.avg != null ? `${r.avg}/30` : "—"} · {r.votes} votes</div>
        </div>
        <div className="flex items-center gap-2">
          <Input placeholder="Reel URL" className="w-56 h-8 text-xs" value={url} onChange={(e) => setUrl(e.target.value)} data-testid={`admin-reel-url-${r.property_id}`} />
          <Button size="sm" variant="outline" onClick={saveUrl} disabled={!url} data-testid={`admin-reel-url-save-${r.property_id}`}>Save URL</Button>
          {r.video_url && <a href={r.video_url} target="_blank" rel="noreferrer" className="text-[#c8102e]"><ExternalLink className="w-4 h-4" /></a>}
        </div>
        <div className="flex items-center gap-1.5">
          <Select value={slot} onValueChange={setSlot}>
            <SelectTrigger className="w-24 h-8 text-xs" data-testid={`admin-reel-slot-${r.property_id}`}><SelectValue /></SelectTrigger>
            <SelectContent><SelectItem value="judge1">Judge 1</SelectItem><SelectItem value="judge2">Judge 2</SelectItem><SelectItem value="judge3">Judge 3</SelectItem></SelectContent>
          </Select>
          {["teamwork", "creativity", "teaches"].map((k) => (
            <Input key={k} type="number" min="0" max="10" step="0.5" placeholder={k.slice(0, 4)} className="w-16 h-8 text-xs" value={s[k]} onChange={(e) => setS({ ...s, [k]: e.target.value })} data-testid={`admin-reel-${k}-${r.property_id}`} />
          ))}
          <Button size="sm" onClick={saveScore} disabled={s.teamwork === "" || s.creativity === "" || s.teaches === ""} data-testid={`admin-reel-score-save-${r.property_id}`}>Score</Button>
        </div>
      </div>
    </div>
  );
}

export default function AdminReels() {
  const [rows, setRows] = useState([]);
  const [q, setQ] = useState("");
  const [filter, setFilter] = useState("all");
  const load = () => api.get("/admin/reels").then(({ data }) => setRows(data));
  useEffect(() => { load(); }, []);

  const filtered = rows
    .filter((r) => (!q || r.property_name.toLowerCase().includes(q.toLowerCase())) &&
      (filter === "all" || (filter === "has" ? !!r.video_url : !r.video_url)))
    .sort((a, b) => (!!b.video_url - !!a.video_url) || a.property_name.localeCompare(b.property_name));

  return (
    <div className="space-y-2">
      <p className="text-sm text-slate-600 mb-2">Enter the reel URL and each judge's scores (Teamwork / Creativity / Teaches, each /10). Judges can also sign in and score directly from the Judging page.</p>
      <div className="flex flex-wrap gap-2 mb-3">
        <Input placeholder="Search property…" value={q} onChange={(e) => setQ(e.target.value)} className="max-w-xs" data-testid="reels-search" />
        <Select value={filter} onValueChange={setFilter}>
          <SelectTrigger className="w-40" data-testid="reels-filter"><SelectValue /></SelectTrigger>
          <SelectContent>
            <SelectItem value="all">All</SelectItem>
            <SelectItem value="has">Has reel</SelectItem>
            <SelectItem value="missing">Missing reel</SelectItem>
          </SelectContent>
        </Select>
        <span className="text-sm text-slate-500 self-center ml-auto" data-testid="reels-count">{filtered.length} of {rows.length}</span>
      </div>
      {filtered.length === 0 && <p className="text-sm text-slate-500 py-10 text-center border border-dashed border-slate-300 rounded-md">No properties match.</p>}
      {filtered.map((r) => <Row key={r.property_id} r={r} onSaved={load} />)}
    </div>
  );
}
